import { OptionsApi } from '@core/swagger-client';
import { WebinarStatus } from '@core/types';
import {
    Button,
    Check,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    Delete,
    Edit,
    ErrorMessagePage,
    LoaderPage,
    PageHeader,
    PageHeaderContent
} from '@core/ui';
import { getRouteApi, useNavigate } from '@online-webinar/router';
import { BackToOriginLink, Link } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

const route = getRouteApi('/$webinarId/follow-up/');
export const FollowUpOverviewPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { webinarId } = route.useParams();

    const { data: webinar, error: webinarError, isLoading: webinarIsLoading } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: followUps, error: followUpsError, isLoading: followUpsIsLoading, refetch: refetchFollowUps } = useQuery(OptionsApi.followUp.get(webinarId!));

    const createNew = () => {
        navigate({ to: '/$webinarId/follow-up/$followUpId', params: { webinarId, followUpId: crypto.randomUUID() } });
    };

    const removeMutation = useMutation(OptionsApi.followUp.remove());
    const remove = (followUpId: string) => {
        removeMutation.mutate(
            { webinarId, followUpId },
            {
                onSuccess: () => {
                    refetchFollowUps();
                }
            }
        );
    };

    const error = webinarError || followUpsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !followUps || webinarIsLoading || followUpsIsLoading) {
        return <LoaderPage></LoaderPage>;
    }

    if (webinar.status === WebinarStatus.Cancelled || webinar.status === WebinarStatus.Expired) {
        return <ErrorMessagePage statusCode={410} errorCode={webinar.status === WebinarStatus.Cancelled ? 'CancelledException' : 'ExpiredException'} />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>
                    <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                </PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <ContentHeader contentHeader={t('FollowUpOverview.PageHeading')} />
                <p className="max-w-2xl mb-6">{t('FollowUpOverview.InfoText')}</p>

                <Button variant="primary" onClick={createNew}>
                    {t('FollowUpOverview.Button.CreateFollowUp')}
                </Button>

                {followUps.length > 0 ? (
                    <div className="table-responsive max-w-6xl bg-white rounded-lg mt-10 shadow-md">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>{t('FollowUpOverview.TableHeader.Name')}</th>
                                    <th>{t('FollowUpOverview.TableHeader.BccOwner')}</th>
                                    <th>{t('FollowUpOverview.TableHeader.Processed')}</th>
                                    <th className="w-30"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {followUps.map((followUp) => (
                                    <tr key={followUp.followUpId}>
                                        <td>{followUp.title}</td>
                                        <td>{followUp.bccOwner ? <Check classNames="size-6 stroke-current" /> : <></>}</td>
                                        <td>
                                            <Link
                                                classNames="leading-[1.25rem] -my-4"
                                                to="/$webinarId/follow-up/$followUpId/processed"
                                                params={{ webinarId, followUpId: followUp.followUpId }}
                                                variant="link"
                                            >
                                                {followUp.processedFollowUps}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link
                                                iconbtn
                                                classNames="-my-4 translate-y-1.5"
                                                to="/$webinarId/follow-up/$followUpId"
                                                params={{ webinarId, followUpId: followUp.followUpId }}
                                            >
                                                <Edit classNames="size-6 stroke-current" />
                                                <span className="sr-only">
                                                    <Trans i18nKey="FollowUpOverview.Button.Edit" values={{ name: followUp.title }} />
                                                </span>
                                            </Link>
                                            <Button
                                                onClick={() => remove(followUp.followUpId)}
                                                iconbtn
                                                classNames="-my-4 translate-y-1.5"
                                                isLoading={removeMutation.isPending}
                                            >
                                                <Delete classNames="size-6 stroke-warning" />
                                                <span className="sr-only">
                                                    <Trans i18nKey="FollowUpOverview.Button.Delete" values={{ name: followUp.title }} />
                                                </span>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="mt-10">{t('FollowUpOverview.NoFollowUps')}</p>
                )}
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
